"use client";

import type { ReactNode, Ref } from "react";

/**
 * Rule 58 layout reserve for the horary answer.
 *
 * The engine answer lands seconds after submit — outside CLS's 500ms
 * input-exclusion window — and is far taller than the question form, so
 * inserting it on arrival pushed the evergreen guide, KP studio and FAQ that
 * were on screen down by its full height (CrUX phone p75 CLS 0.80 on
 * /prashna-kundali/ while the lab, which never waits, read 0.000).
 *
 * The reserve mounts the moment the seeker submits — inside the window, so the
 * one-time push of the content below is excluded — floors the region at a full
 * viewport, and is anchored to the top of the viewport at that same instant.
 * The answer then renders INSIDE the reserve: growth past the floor happens
 * below the fold, and a short answer or an engine error never shrinks it.
 * Same contract as the kundali result swap (`KundaliFormCard.result.tsx`).
 */
export const PRASHNA_RESULT_RESERVE_CLASS = "min-h-screen";

export function PrashnaResultReserve({
  ref,
  children,
}: {
  ref: Ref<HTMLDivElement>;
  children: ReactNode;
}) {
  return (
    <div ref={ref} data-prashna-result-reserve className={PRASHNA_RESULT_RESERVE_CLASS}>
      {children}
    </div>
  );
}

export function PrashnaCastingStatus({ label }: { label: string }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className="mt-8 flex items-center gap-3 text-body-small text-text-secondary"
    >
      <span
        aria-hidden
        className="inline-block h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
      />
      {label}
    </p>
  );
}

/**
 * The seeker is looking at the reserve when an engine error arrives (the form
 * scrolled away at submit), so the message is repeated here as plain text — the
 * form's own `role="alert"` already announced it — with a way back to the
 * question. The reserve itself stays mounted so the guide below never jumps up.
 */
export function PrashnaReserveError({
  message,
  editLabel,
  onEdit,
}: {
  message: string;
  editLabel: string;
  onEdit: () => void;
}) {
  return (
    <div className="mt-8 rounded-lg border border-border-default bg-surface-raised p-4 text-body-small text-text-secondary">
      <p>{message}</p>
      <button
        type="button"
        onClick={onEdit}
        className="mt-3 inline-flex min-h-11 items-center font-semibold text-intent-brand underline underline-offset-4"
      >
        {editLabel}
      </button>
    </div>
  );
}
